export type ExportFileNameContext = {
	/** Título (heading ou linha em negrito) logo acima da tabela na resposta do Walter. */
	heading?: string | null
	/** Pergunta do usuário que originou a resposta, ou o título da conversa. */
	question?: string | null
	/** Posição da tabela na resposta (1-based), quando a resposta tem mais de uma. */
	tableIndex?: number | null
	date?: Date
}

const MAX_SLUG_LENGTH = 60
const FALLBACK_SLUG = 'walteria_tabela'

// Palavras de pergunta/pedido que só fazem barulho no começo do nome:
// "Mostre o IMA dos últimos inventários" -> "ima_dos_ultimos_inventarios".
const LEADING_FILLER = new Set([
	'qual', 'quais', 'quanto', 'quanta', 'quantos', 'quantas', 'como', 'onde',
	'mostre', 'mostra', 'mostrar', 'liste', 'lista', 'listar', 'exiba', 'exibir',
	'gere', 'gera', 'gerar', 'traga', 'trazer', 'busque', 'buscar', 'quero',
	'preciso', 'pode', 'poderia', 'voce', 'por', 'favor', 'me', 'ver', 'de',
	'o', 'a', 'os', 'as', 'um', 'uma', 'e', 'sao', 'esta', 'estao',
	'cual', 'cuales', 'muestra', 'muestre', 'el', 'la', 'los', 'las',
	'what', 'which', 'show', 'list', 'the', 'is', 'are',
])

export class ExportFileNameProvider {
	/**
	 * Nome do arquivo exportado de uma tabela do WalterIA, sem extensão.
	 *
	 * Regra: <contexto>[_tabelaN]_<AAAA-MM-DD_HHMMSS>
	 * - contexto: o título acima da tabela; sem ele, a pergunta que originou a resposta;
	 *   sem nenhum dos dois, "walteria_tabela". Quando a consulta abrange várias fazendas,
	 *   o nome reflete o que o título/pergunta diz — não se tenta inferir uma fazenda.
	 * - _tabelaN: só quando a resposta tem várias tabelas e o nome não veio do título
	 *   de cada uma (senão todas teriam o mesmo nome).
	 * - data e hora com segundos: downloads sucessivos não se sobrescrevem.
	 */
	static build({ heading, question, tableIndex, date = new Date() }: ExportFileNameContext): string {
		const headingSlug = this.slugify(heading)
		const slug = headingSlug || this.slugify(question, true) || FALLBACK_SLUG
		const suffix = !headingSlug && tableIndex ? `_tabela${tableIndex}` : ''
		return `${slug}${suffix}_${this.timestamp(date)}`
	}

	/**
	 * Só [a-z0-9_]: sem acentos nem caracteres proibidos no Windows (\ / : * ? " < > |),
	 * e cortado numa fronteira de palavra para não estourar o limite de nome de arquivo.
	 */
	static slugify(text?: string | null, stripLeadingFiller = false): string {
		if (!text) return ''
		let words = text
			.normalize('NFD')
			.replace(/[̀-ͯ]/g, '')
			.toLowerCase()
			.split(/[^a-z0-9]+/)
			.filter(Boolean)

		if (stripLeadingFiller) {
			let start = 0
			while (start < words.length && LEADING_FILLER.has(words[start])) start++
			// Pergunta feita só de palavras de enchimento: melhor manter do que ficar vazio.
			if (start < words.length) words = words.slice(start)
		}

		let slug = ''
		for (const word of words) {
			const next = slug ? `${slug}_${word}` : word
			if (next.length > MAX_SLUG_LENGTH) break
			slug = next
		}
		// Uma única palavra maior que o limite: corta no meio mesmo.
		if (!slug && words.length) slug = words[0].slice(0, MAX_SLUG_LENGTH)
		return slug
	}

	static timestamp(date: Date): string {
		const pad = (n: number) => String(n).padStart(2, '0')
		const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
		const time = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
		return `${day}_${time}`
	}
}
